import { useLazyRef, useUpdateEffect, χref } from '@studio/react-utils/hooks';
import { freeze } from 'immer';
import type { ReactElement } from 'react';
import { createWithEqualityFn as create } from 'zustand/traditional';
import { immer } from 'zustand/middleware/immer';
import { Context } from './context.js';
import type { Draft, Props, State, Store } from './types.js';
import { Panel } from './types.js';
import { toCandidate } from '#~/device/index.js';
import { DeviceRegistryInterfaceSectionType } from '@tryiris-ai/common.protojs/api/v1/device_settings';

/** Video settings workflow `<Provider>`. */
export const Provider = ({
  agentId,
  children,
  device,
  status,
  defaultPanel,
  defaultTab,
  roomId,
  onDeviceCommitted
}: Props): ReactElement => {
  const store = useLazyRef<Store>(() =>
    create<State>()(
      immer((set) => {
        const candidate = toCandidate(device);

        return freeze({
          agentId,
          // Multi-Add Phase 2: persist the room the dialog was opened against
          // so the Authentication tab can resolve roomId for a transitory Iris
          // placeholder (no joined agent → `agent.roomId` is undefined). May be
          // undefined for the account settings path (no room context).
          // Per `reference-rules-in-comments` (ai.knowledge/global).
          roomId,
          onDeviceCommitted,
          candidate,
          control: {
            cache: {}
          },
          external: { status },
          flags: {
            canUndo: false,
            isBusy: false,
            isDirty: false,
            isDeviceSettingsDirty: false,
            isVideoSettingsDirty: false,
            isNameDirty: false,
            pendingAuthContinuation: false
          },
          lifecycle: {
            original: structuredClone(device),
            rollback: undefined,
            snapshot: structuredClone(candidate)
          },
          nameOverride: undefined,
          manufacturer: {
            config: undefined,
            isOther: false,
            preferredModelId: {}
          },
          // Initial panel that is set to render. Honor `defaultPanel`
          // from the deep-link path; otherwise fall back to
          // `Panel.IrisConnection` (the original default).
          panel: defaultPanel ?? Panel.IrisConnection,
          // Stash the initial Iris-Connection sub-tab so
          // `<IrisConnectionSection>` can seed `activeTab` on mount
          // without prop drilling through `<Scaffold>`'s dynamic
          // section config.
          defaultTab,
          // Seed with the deep-link target if one was provided.
          // `<IrisConnectionSection>` re-writes this on every `onChange`
          // so the footer's global Save handler always reads the user's
          // current sub-tab.
          irisConnectionActiveTab: defaultTab,
          produce: (fn: (draft: Draft) => void): void => {
            set(fn);
          },
          overrides: {},
          general: {
            cache: {},
            section: 'General',
            sectionKey: DeviceRegistryInterfaceSectionType.ADV_SETTINGS_GENERAL
          },
          video: {
            cache: {},
            section: 'General',
            sectionKey: DeviceRegistryInterfaceSectionType.ADV_SETTINGS_OUTPUT_RTSP
          },
          audio: {
            cache: {},
            section: 'General',
            sectionKey: DeviceRegistryInterfaceSectionType.ADV_SETTINGS_AUDIO_GENERAL
          },
          system: {
            cache: {},
            section: 'General',
            sectionKey: DeviceRegistryInterfaceSectionType.ADV_SETTINGS_SYSTEM
          },
          network: {
            cache: {},
            section: 'General',
            sectionKey: DeviceRegistryInterfaceSectionType.ADV_SETTINGS_NETWORK
          },
          features: {
            cache: {},
            section: 'General',
            sectionKey: DeviceRegistryInterfaceSectionType.ADV_SETTINGS_FEATURES
          },
          monitoring: {
            cache: {},
            section: 'General',
            sectionKey: DeviceRegistryInterfaceSectionType.ADV_SETTINGS_MONITORING
          },
          controls: {
            cache: {},
            section: 'General',
            sectionKey: DeviceRegistryInterfaceSectionType.ADV_SETTINGS_CONTROLS
          },
          sectionStates: {}
        });
      })
    )
  );

  // Sync external status.
  useUpdateEffect(() => {
    const { produce } = χref(store).getState();

    produce(({ external }) => {
      external.status = status;
    });
  }, [status]);

  return <Context.Provider value={χref(store)}>{children}</Context.Provider>;
};
